
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import * as Sentry from "@sentry/react";
import { BusinessData } from '@/api/business/types';
import { businessMapper } from '@/api/business/mappers';

export const useBusinessDataFetcher = () => {
  const [businessData, setBusinessData] = useState<BusinessData>({
    name: '',
    industry: '',
    website: '',
    description: '',
    keywords: [],
    colors: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  
  const supabaseClient = supabase;
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Fetch business data
  useEffect(() => {
    const fetchBusinessData = async () => {
      setIsLoading(true);
      setAuthError(null);
      
      try {
        // First check if user is authenticated
        const { data: sessionData, error: sessionError } = await supabaseClient.auth.getSession();
        
        if (sessionError) {
          console.error('Error checking authentication:', sessionError);
          setAuthError('Authentication error: ' + sessionError.message);
          toast({
            title: 'Authentication error',
            description: 'Please sign in to view your business settings.',
            variant: 'destructive',
          });
          navigate('/login');
          return;
        }
        
        if (!sessionData.session) {
          console.warn('No active session found');
          setAuthError('No active session');
          toast({
            title: 'Authentication required',
            description: 'Please sign in to view your business settings.',
            variant: 'destructive',
          });
          navigate('/login');
          return;
        }
        
        const userId = sessionData.session.user.id;
        console.log('Fetching business data for user:', userId);
        
        const { data, error } = await supabaseClient
          .from('business')
          .select('*')
          .eq('owner_id', userId)
          .maybeSingle();
          
        if (error) {
          console.error('Error fetching business data:', error);
          throw error;
        }
        
        if (data) {
          console.log('Business data loaded:', data);
          // Format the data using our mapper function
          const formattedData = businessMapper.toFrontend(data);
          setBusinessData(formattedData);
        } else {
          console.warn('No business data found for user');
          // Consider redirecting to onboarding if no business data exists
        }
      } catch (error) {
        console.error('Error in fetchBusinessData:', error);
        Sentry.captureException(error);
        toast({
          title: 'Failed to load business settings',
          description: 'Please try again later or contact support.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBusinessData();
  }, [supabaseClient, toast, navigate]);

  return {
    businessData,
    setBusinessData,
    isLoading,
    authError
  };
};
